/**
 * Email body editor with a Visual (rich-text) / HTML (raw source) toggle.
 *
 * Defaults to the Visual rich-text editor. HTML mode is opt-in for power users who want to
 * paste/edit raw HTML verbatim (the visual editor only understands a subset of HTML, so it
 * re-formats raw markup when you switch back to Visual — that's an inherent TipTap limitation,
 * hence the note when in HTML mode).
 */
import { useState } from 'react';
import { Stack, Group, Text, Textarea, SegmentedControl } from '../../../../components/ui';
import { HtmlEditor } from '../../../../components/ui/inputs';

export function EmailBodyEditor({
  value,
  onChange,
  label = 'Email Body',
  placeholder = 'Enter your email content…',
  minHeight = 260,
}: {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  minHeight?: number;
}) {
  // Always start in Visual — it's the primary, WYSIWYG experience.
  const [mode, setMode] = useState<'visual' | 'html'>('visual');

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        {label ? <Text size="sm" fw={500}>{label}</Text> : <span />}
        <SegmentedControl
          size="xs"
          value={mode}
          onChange={(v) => setMode(v as 'visual' | 'html')}
          data={[{ label: 'Visual', value: 'visual' }, { label: 'HTML', value: 'html' }]}
        />
      </Group>

      {mode === 'visual' ? (
        <HtmlEditor value={value} onChange={(html) => onChange(html)} placeholder={placeholder} minHeight={minHeight} />
      ) : (
        <>
          <Textarea
            value={value}
            onChange={(e) => onChange(e.currentTarget.value)}
            placeholder="<table>…raw HTML email…</table>"
            autosize
            minRows={12}
            maxRows={24}
            styles={{ input: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 } }}
          />
          <Text size="xs" c="dimmed">Raw HTML — kept verbatim. Switching back to Visual re-formats it to the editor's supported tags.</Text>
        </>
      )}
    </Stack>
  );
}
